import React from 'react';
import { Document, View } from '@react-pdf/renderer';

import { OperationType } from 'components/RecordsListView/constants';
import {
  ExportLanguageEnum,
  OrientationOptions
} from '../../Hooks/useOperationPdf';
import Layout from './components/Layout';
import ApplicationDetails from './components/ApplicationDetails';
import Tags from './components/Tags';
import Notes from './components/Notes';
// To be imported for Work Order PDF
// import Environmental from './components/Environmental';
import TankInformation from './components/TankInformation';
import { getFieldTableRows } from './helpers/tableConfigs/fieldsTableConfig';
import { getProductTableRows } from './helpers/tableConfigs/productsTableConfig';
import { getWeatherTableRows } from './helpers/tableConfigs/weatherTableConfig';
import parentStyles from './utils/parentStyles';
import Header from './components/Header';
import FlexTable from './components/FlexTable';
import Signature from './components/Signature';
import WorkerProtection from './components/WorkerProtection';
import { en, es } from './locales';

// Define types for Application and props
interface Weather {
  start: {
    windSpeed: number;
    windSpeedUnit: string;
    windDirection: string;
    skyCondition: string;
    temperature: number;
    temperatureUnit: string;
    soilMoisture: string;
    humidity: number;
  };
  end: {
    windSpeed: number;
    windSpeedUnit: string;
    windDirection: string;
    skyCondition: string;
    temperature: number;
    temperatureUnit: string;
    soilMoisture: string;
    humidity: number;
  };
}

export interface OperationPDFData {
  name: string;
  organizationName: string;
  cropSeasons: any;
  totalAppliedArea: number;
  _exportLanguage: ExportLanguageEnum;
  fullDate: Record<string, string>;
  weather?: Weather;
  tankInformation?: any;
  products: any[];
  applicationStrategy: any;
  fields: any[];
  totalFieldsArea: number;
  notes: string;
  applicators?: any[];
  authorization?: { authorizedBy: string; authorizationDate: string };
  equipmentIds?: string;
  workerProtection?: any;
  createdBy?: string;
  tags?: any[];
  mapImage?: string;
  startDate: string;
  endDate?: string;
  type: string;
  timingEvent: string;
  applicationMethod: string;
  reiExpirationDate: string;
  phiExpirationDate: string;
  createdAt: string;
}

interface OperationPdfProps {
  application?: OperationPDFData | null;
  orientation?: OrientationOptions;
  operationType: OperationType;
}

const OperationPdf: React.FC<OperationPdfProps> = ({
  application: rawApplication,
  orientation = 'portrait',
  operationType = OperationType.APPLICATION
}) => {
  if (!rawApplication) return <Document />;

  const isLatamSpanish =
    rawApplication?._exportLanguage === ExportLanguageEnum.LATAM_SPANISH;

  const locale = isLatamSpanish ? es : en;

  const application = {
    ...rawApplication,
    applicators: rawApplication.applicators || []
  };

  const hasTankInformation = application.tankInformation !== undefined;

  const headerContinuedText = locale.common.continued;

  /*
    Get crop names and ids from fields. cropId is what links a product's
    user-edited (CWF) REI/PHI override to the crop being applied
  */
  const cropNames = application.fields
    .map(field => field.cropName)
    .filter(Boolean);
  const cropIds = application.fields.map(field => field.cropId).filter(Boolean);

  return (
    <Layout
      generatedFullDate={application.fullDate}
      exportLanguage={application._exportLanguage.toString()}
      locale={locale}
      orientation={orientation}
    >
      {/* Header */}
      <Header
        applicationName={application.name}
        organization={application.organizationName}
        cropSeasons={application.cropSeasons}
        totalAppliedArea={application.totalAppliedArea}
        operationType={operationType}
        locale={locale}
      />
      {/* Details Section */}
      <View style={parentStyles.viewContainer}>
        <ApplicationDetails
          application={application}
          operationType={operationType}
          locale={locale}
          headerContinuedText={headerContinuedText}
          orientation={orientation}
        />
        {/* Weather Section */}
        <FlexTable
          headerText={locale.weather.title}
          tableRows={getWeatherTableRows(
            application.weather,
            locale,
            operationType
          )}
          headingStyles={{ height: 22 }}
          cellStyles={{ minHeight: 24 }}
          cellBordered={operationType === OperationType.WORK_ORDER}
          wrap={false}
          headerContinuedText={headerContinuedText}
        />
        {/* Tags Section */}
        <Tags title={locale.tags.title} tags={application.tags} />
        {/* Notes Section */}
        <Notes
          notes={application.notes}
          operationType={operationType}
          locale={locale}
          headerContinuedText={headerContinuedText}
        />
        {/* Products Section */}
        <FlexTable
          headerText={locale.products.title}
          tableRows={getProductTableRows(
            application.products,
            hasTankInformation,
            cropNames,
            orientation,
            locale,
            isLatamSpanish,
            cropIds
          )}
          headingStyles={{
            height: orientation === 'portrait' || isLatamSpanish ? 36 : 22
          }}
          continuedText={locale.products.continued}
          headerContinuedText={headerContinuedText}
          introContent={
            // Tank Information Section
            hasTankInformation ? (
              <TankInformation
                tankInformation={application.tankInformation}
                locale={locale}
              />
            ) : null
          }
        />
        {/* Fields Section */}
        <FlexTable
          headerText={locale.properties.title}
          tableRows={getFieldTableRows(
            application.fields,
            locale,
            application.totalAppliedArea,
            operationType
          )}
          continuedText={locale.properties.continued}
          headingStyles={{ height: 22 }}
          headerContinuedText={headerContinuedText}
        />
        {/* Signature and Worker Protection Sections (Work Order only) */}
        {operationType === OperationType.WORK_ORDER && (
          <>
            <Signature locale={locale} />
            <WorkerProtection
              workerProtectionData={application.workerProtection}
              locale={locale}
            />
          </>
        )}
      </View>
    </Layout>
  );
};

export default OperationPdf;
